import React, { ChangeEvent, FC, useCallback, useEffect, useRef, useState } from 'react';

import { Button, FormControl, Grid, IconButton, InputAdornment, MenuItem, TextField, Typography, useTheme } from '@material-ui/core';

import { DateRange } from '@material-ui/pickers';

import moment from 'moment';

import getSymbolFromCurrency from 'currency-symbol-map';

import { ChevronLeft, ChevronRight, ExpandMore } from '@material-ui/icons';

import { AggregatedValues, ClientFilterType, DashboardValues, HighlightTypes } from './IBDashboardModels';

import { useStyles } from './styles';

import { DateRangeSelect, HamburgerIcon, Header, PageTitle, SelectControl } from '../../components';

import { getAggregatedValues, getDashboardValues, getGeneralSettings, getPartnerPartnerships, getPartnerSettings, setPartnerSettings } from './IBDashboardService';

import { useCommissionJob } from './hooks/useCommissionJob';

import { PartnerPartnership, UserLoginData } from '../../models';

import { useApiError, useAppData, useLoading } from '../../providers';

import { Balance, Cashback, Clients, CPQuickReport, DoubleGraphPanel, DoubleQuickReport, FiltersDialog, GraphPanel, Links, QuickReport, TradingAccounts } from './components';

import { PATH_CLIENTS_LIST, PATH_COMMISSIONS_BY_CLIENT, PATH_TRADING_SUMMARY } from '../../utils';

import { useCommonStyles, variables } from '../../styles';

import { setCrossDomainAmplitudeIdentity } from '../../services/amplitudeService';

/*
 * Temporary release switch.
 *
 * false:
 * - show old Deposits / Withdrawals / Net Deposits cards;
 * - use old translations;
 * - hide Trading Accounts and Portfolio cards.
 *
 * true:
 * - show the new dashboard cards;
 * - use the new translations.
 */
const SHOW_NEW_DASHBOARD_CARDS = false;
console.log('SHOW_NEW_DASHBOARD_CARDS', SHOW_NEW_DASHBOARD_CARDS);

type DashboardPanel =
  | 'Commissions'
  | 'Clients & Accounts'
  | 'Deposits'
  | 'Withdrawals'
  | 'Net Deposits'
  | 'Trading Accounts Deposits'
  | 'Trading Accounts Withdrawals'
  | 'Net Trading Accounts Deposits'
  | 'Portfolio Deposits'
  | 'Portfolio Withdrawals'
  | 'Net Portfolio Deposits'
  | 'Closed Positions'
  | 'Clients P&L';

type Translation = Record<string, any>;

const SETTINGS_KEY = 'report-currency';

const CLIENTS_ACCOUNTS_HIGHLIGHT_TYPES = [HighlightTypes.Clients, HighlightTypes.TradingAccounts];

const CLIENTS_ACCOUNTS_ORDER = {
  Clients: 1,
  TradingAccounts: 2,
};

const CLIENTS_ACCOUNTS_COLORS = [variables.richBlack, variables.mediumSeaGreen];

const PROFIT_LOSS_HIGHLIGHT_TYPES = [HighlightTypes.Profit, HighlightTypes.Loss];

const PROFIT_LOSS_ORDER = {
  Profit: 1,
  Loss: 2,
};

const PROFIT_LOSS_COLORS = [variables.mediumSeaGreen, variables.americanPink];

const CLOSED_POSITIONS_HIGHLIGHT_TYPES = [HighlightTypes.Volume, HighlightTypes.VolumeLots];

const CLOSED_POSITIONS_ORDER = {
  Volume: 1,
  VolumeLots: 2,
};

const CLOSED_POSITIONS_COLORS = [variables.richBlack, variables.mediumSeaGreen];

const INITIAL_DASHBOARD_VALUES: DashboardValues = {
  activeClients: 0,
  activeTradingAccounts: 0,
  cashback: 'Not active',
  inactiveClients: 0,
  inactiveTradingAccounts: 0,
  totalClients: 0,
  totalLinks: 0,
  totalTradingAccounts: 0,
};

const INITIAL_AGGREGATED_VALUES: AggregatedValues = {
  accounts: 0,
  accountsPercent: 0,

  clients: 0,
  clientsPercent: 0,

  commissions: 0,
  commissionsChangePercent: 0,

  deposits: 0,
  depositsChangePercent: 0,

  withdrawals: 0,
  withdrawalsChangePercent: 0,

  netDeposits: 0,
  netDepositsChangePercent: 0,

  internalTransferIn: 0,
  internalTransferInChangePercent: 0,

  internalTransferOut: 0,
  internalTransferOutChangePercent: 0,

  netInternalTransfer: 0,
  netInternalTransferChangePercent: 0,

  volume: 0,
  volumeChangePercent: 0,

  volumeLots: 0,
  volumeLotsChangePercent: 0,

  profit: 0,
  profitChangePercent: 0,

  loss: 0,
  lossChangePercent: 0,

  updatedAt: '',
};

const parseJson = <T,>(value: string | undefined, fallback: T): T => {
  if (!value) {
    return fallback;
  }

  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
};

export const IBDashboard: FC = () => {
  const classes = useStyles();
  const commonClasses = useCommonStyles();
  const theme = useTheme();

  const { translations, currencyList, saveCurrencyList, savePartnershipList, saveClientPortalRedirectUrl, userData } = useAppData();

  const { addError } = useApiError();
  const { setIsLoading } = useLoading();

  const navRef = useRef<HTMLDivElement>(null);

  const aggregatedRequestIdRef = useRef(0);

  const addErrorRef = useRef(addError);

  const setIsLoadingRef = useRef(setIsLoading);

  const saveCurrencyListRef = useRef(saveCurrencyList);

  const savePartnershipListRef = useRef(savePartnershipList);

  const saveClientPortalRedirectUrlRef = useRef(saveClientPortalRedirectUrl);

  const [selectedDateRange, setSelectedDateRange] = useState<DateRange<Date>>([moment().subtract(1, 'months').toDate(), moment().toDate()]);

  const [currencies, setCurrencies] = useState<string[]>([]);

  const [selectedCurrency, setSelectedCurrency] = useState<string>('USD');

  const [partnerships, setPartnerships] = useState<PartnerPartnership[]>([]);

  const [selectedPartnership, setSelectedPartnership] = useState<string>('All');

  const [selectedClientFilterType, setSelectedClientFilterType] = useState<ClientFilterType>(ClientFilterType.All);

  const [dashboardValues, setDashboardValues] = useState<DashboardValues>(INITIAL_DASHBOARD_VALUES);

  const [aggregatedValues, setAggregatedValues] = useState<AggregatedValues>(INITIAL_AGGREGATED_VALUES);

  const [selectedPanel, setSelectedPanel] = useState<DashboardPanel>('Commissions');

  const [translation, setTranslation] = useState<Translation>({});

  const [openFilterDialog, setOpenFilterDialog] = useState(false);

  const [userRole, setUserRole] = useState<string>('');

  const [hasCashbackAccess, setHasCashbackAccess] = useState(false);

  useEffect(() => {
    addErrorRef.current = addError;
  }, [addError]);

  useEffect(() => {
    setIsLoadingRef.current = setIsLoading;
  }, [setIsLoading]);

  useEffect(() => {
    saveCurrencyListRef.current = saveCurrencyList;
  }, [saveCurrencyList]);

  useEffect(() => {
    savePartnershipListRef.current = savePartnershipList;
  }, [savePartnershipList]);

  useEffect(() => {
    saveClientPortalRedirectUrlRef.current = saveClientPortalRedirectUrl;
  }, [saveClientPortalRedirectUrl]);

  useEffect(() => {
    setTranslation(parseJson<Translation>(translations, {}));
  }, [translations]);

  useEffect(() => {
    const parsedUser = parseJson<UserLoginData | null>(userData, null);

    const account = parsedUser && Array.isArray(parsedUser.accounts) ? parsedUser.accounts[0] : undefined;

    setUserRole(account?.userRoleName || '');

    const routes = account?.role?.routeCollection?.routeCollectionHasRoute;

    setHasCashbackAccess(Array.isArray(routes) && routes.some((route) => route.route === 'cashbackPartners'));
  }, [userData]);

  useEffect(() => {
    if (!userData || partnerships.length === 0) {
      return;
    }

    const parsedUser = parseJson<UserLoginData | null>(userData, null);

    if (!parsedUser) {
      return;
    }

    const partnershipCodes = partnerships.map((partnership) => partnership.partnershipCode);

    setCrossDomainAmplitudeIdentity(parsedUser, partnershipCodes, window.location.host);
  }, [userData, partnerships]);

  const getSettings = useCallback(async () => {
    setIsLoadingRef.current(true);

    try {
      const settings = await getPartnerSettings(SETTINGS_KEY);

      if (settings?.data?.value) {
        setSelectedCurrency(settings.data.value);
      }
    } catch (error: any) {
      addErrorRef.current(JSON.stringify(error?.response || error));
    } finally {
      setIsLoadingRef.current(false);
    }
  }, []);

  const getCurrencyData = useCallback(async () => {
    setIsLoadingRef.current(true);

    try {
      const response = await getGeneralSettings();

      const responseCurrencies = Array.isArray(response?.data?.currencies) ? response.data.currencies : [];

      const clientPortalRedirectUrl = response?.data?.clientPortalRedirectUrl || '';

      saveClientPortalRedirectUrlRef.current(clientPortalRedirectUrl);

      setCurrencies(responseCurrencies);

      saveCurrencyListRef.current(JSON.stringify(responseCurrencies));

      const settings = await getPartnerSettings(SETTINGS_KEY);

      if (settings?.data?.value) {
        setSelectedCurrency(settings.data.value);
      }
    } catch (error: any) {
      addErrorRef.current(JSON.stringify(error?.response || error));
    } finally {
      setIsLoadingRef.current(false);
    }
  }, []);

  const getPartnershipData = useCallback(async () => {
    setIsLoadingRef.current(true);

    try {
      const response = await getPartnerPartnerships(true);

      const partnershipData = Array.isArray(response?.data) ? response.data : [];

      setPartnerships(partnershipData);

      savePartnershipListRef.current(JSON.stringify(partnershipData));
    } catch (error: any) {
      addErrorRef.current(JSON.stringify(error?.response || error));
    } finally {
      setIsLoadingRef.current(false);
    }
  }, []);

  const getDashboardData = useCallback(async (clientFilterType: ClientFilterType) => {
    setIsLoadingRef.current(true);

    try {
      const response = await getDashboardValues(clientFilterType);

      setDashboardValues({
        ...INITIAL_DASHBOARD_VALUES,
        ...(response?.data || {}),
      });
    } catch (error: any) {
      setDashboardValues(INITIAL_DASHBOARD_VALUES);

      addErrorRef.current(JSON.stringify(error?.response || error));
    } finally {
      setIsLoadingRef.current(false);
    }
  }, []);

  const getValues = useCallback(async (partnershipCode: string, dateRange: DateRange<Date>, clientFilterType: ClientFilterType, silent = false) => {
    const fromDate = dateRange[0];

    const toDate = dateRange[1];

    if (!fromDate || !toDate) {
      return;
    }

    const requestId = ++aggregatedRequestIdRef.current;

    if (!silent) {
      setIsLoadingRef.current(true);
    }

    try {
      const response = await getAggregatedValues(partnershipCode === 'All' ? '' : partnershipCode, fromDate, toDate, clientFilterType);

      if (requestId !== aggregatedRequestIdRef.current) {
        return;
      }

      setAggregatedValues({
        ...INITIAL_AGGREGATED_VALUES,
        ...(response?.data || {}),
      });
    } catch (error: any) {
      if (requestId !== aggregatedRequestIdRef.current) {
        return;
      }

      setAggregatedValues(INITIAL_AGGREGATED_VALUES);

      addErrorRef.current(JSON.stringify(error?.response || error));
    } finally {
      if (!silent && requestId === aggregatedRequestIdRef.current) {
        setIsLoadingRef.current(false);
      }
    }
  }, []);

  const { lastJobDate } = useCommissionJob({
    getValues,
    selectedPartnership,
    selectedDateRange,
    selectedClientFilterType,
  });

  useEffect(() => {
    const cachedCurrencies = parseJson<string[]>(currencyList, []);

    if (cachedCurrencies.length > 0) {
      setCurrencies(cachedCurrencies);

      getSettings();

      return;
    }

    getCurrencyData();
  }, [currencyList, getCurrencyData, getSettings]);

  useEffect(() => {
    getPartnershipData();
  }, [getPartnershipData]);

  useEffect(() => {
    getDashboardData(selectedClientFilterType);
  }, [selectedClientFilterType, getDashboardData]);

  const selectedFromDate = selectedDateRange[0];

  const selectedToDate = selectedDateRange[1];

  useEffect(() => {
    if (!selectedFromDate || !selectedToDate) {
      return;
    }

    getValues(selectedPartnership, [selectedFromDate, selectedToDate], selectedClientFilterType);
  }, [selectedPartnership, selectedFromDate, selectedToDate, selectedClientFilterType, getValues]);

  const handleCurrencyChange = useCallback(async (currency: string) => {
    setSelectedCurrency(currency);

    if (!currency) {
      return;
    }

    setIsLoadingRef.current(true);

    try {
      await setPartnerSettings(SETTINGS_KEY, currency);
    } catch (error: any) {
      addErrorRef.current(JSON.stringify(error?.response || error));
    } finally {
      setIsLoadingRef.current(false);
    }
  }, []);

  const handlePartnershipChange = useCallback(
    (
      event: ChangeEvent<{
        value: unknown;
      }>,
    ) => {
      setSelectedPartnership(event.target.value as string);
    },
    [],
  );

  const handleClientFilterTypeChange = useCallback(
    (
      event: ChangeEvent<{
        value: unknown;
      }>,
    ) => {
      setSelectedClientFilterType(event.target.value as ClientFilterType);
    },
    [],
  );

  const applyFilter = useCallback(
    async (partnership: string, currency: string, clientFilterType: ClientFilterType) => {
      setSelectedPartnership(partnership);

      setSelectedClientFilterType(clientFilterType);

      await handleCurrencyChange(currency);

      setOpenFilterDialog(false);
    },
    [handleCurrencyChange],
  );

  const handleNav = useCallback((direction: 'left' | 'right') => {
    if (!navRef.current) {
      return;
    }

    navRef.current.scrollBy({
      left: direction === 'left' ? -200 : 200,
      behavior: 'smooth',
    });
  }, []);

  const lastUpdatedTime = lastJobDate || aggregatedValues.updatedAt;

  /*
   * New translation values are evaluated only
   * inside the hidden new-flow JSX.
   */
  const tradingAccountsDepositsTitle = translation.trading_accounts_deposits || 'Trading Accounts Deposits';

  const tradingAccountsWithdrawalsTitle = translation.trading_accounts_withdrawals || 'Trading Accounts Withdrawals';

  const netTradingAccountsDepositsTitle = translation.net_trading_accounts_deposits || 'Net Trading Accounts Deposits';

  const portfolioDepositsTitle = translation.portfolio_deposits || 'Portfolio Deposits';

  const portfolioWithdrawalsTitle = translation.portfolio_withdrawals || 'Portfolio Withdrawals';

  const netPortfolioDepositsTitle = translation.net_portfolio_deposits || 'Net Portfolio Deposits';

  return (
    <>
      <Header title={translation.dashboard} />

      <Grid container direction="column" className={classes.root}>
        <PageTitle title={translation.dashboard} />

        <Grid container direction="column" className={commonClasses.mainContainer}>
          <Grid item xs={12} className={commonClasses.header}>
            {translation.text_6273}
          </Grid>

          <Grid container direction="row" spacing={2} className={classes.panels}>
            {userRole === 'Partner' && (
              <Grid item className={classes.topPanels}>
                <Balance key={selectedCurrency} selectedCurrency={selectedCurrency} translation={translation} />
              </Grid>
            )}

            <Grid item className={userRole === 'Partner' ? classes.topPanels : classes.topPanelRepresentative}>
              <Clients clients={String(dashboardValues.activeClients || 0)} translation={translation} />
            </Grid>

            <Grid item className={userRole === 'Partner' ? classes.topPanels : classes.topPanelRepresentative}>
              <TradingAccounts accounts={dashboardValues.totalTradingAccounts || 0} active={dashboardValues.activeTradingAccounts || 0} dormant={dashboardValues.inactiveTradingAccounts || 0} translation={translation} />
            </Grid>

            <Grid item className={userRole === 'Partner' ? classes.topPanels : classes.topPanelRepresentative}>
              <Links links={dashboardValues.totalLinks} partnerships={partnerships} translation={translation} />
            </Grid>

            {hasCashbackAccess && (
              <Grid
                item
                className={userRole === 'Partner' ? classes.topPanels : classes.topPanelRepresentative}
                style={{
                  marginRight: 0,
                }}
              >
                <Cashback isActive={dashboardValues.cashback !== 'Not active'} translation={translation} />
              </Grid>
            )}
          </Grid>

          <Grid container direction="row" justify="space-between" alignItems="flex-start">
            <Grid item>
              <Grid container direction="column" spacing={1}>
                <Grid item>
                  <Typography
                    variant="h6"
                    style={{
                      display: 'flex',
                      color: 'black',
                    }}
                  >
                    {translation.quick_reports}
                  </Typography>

                  <Typography variant="caption">{lastUpdatedTime ? `${translation.updated_label_at || 'Updated at'} ${moment(lastUpdatedTime).format('DD/MM/YYYY HH:mm:ss')}` : translation.update_now || 'Update now'}</Typography>
                </Grid>
              </Grid>
            </Grid>

            <Grid item className={classes.mobileContainer}>
              <IconButton onClick={() => setOpenFilterDialog(true)}>
                <HamburgerIcon />
              </IconButton>
            </Grid>
          </Grid>

          <Grid container direction="row" className={`${classes.filterContainer} ${classes.desktopContainer}`} spacing={4}>
            {partnerships.length > 0 && (
              <Grid item xs={3}>
                <p className={commonClasses.formControlTitle}>{translation.text_6275}</p>

                <FormControl variant="outlined" className={commonClasses.formControl}>
                  <SelectControl value={selectedPartnership} onChange={handlePartnershipChange} minWidth={97}>
                    <MenuItem className={commonClasses.optionSelector} value="All">
                      {translation.show_all}
                    </MenuItem>

                    {partnerships.map((partnership) => (
                      <MenuItem
                        className={commonClasses.optionSelector}
                        style={{
                          width: 'inherit',
                        }}
                        key={partnership.partnershipCode}
                        value={partnership.partnershipCode}
                      >
                        {partnership.partnershipCode}
                      </MenuItem>
                    ))}
                  </SelectControl>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={3}>
              <p className={commonClasses.formControlTitle}>{translation.text_6166}</p>

              <DateRangeSelect defaultValue={selectedDateRange} onRangeChange={setSelectedDateRange} />
            </Grid>

            {currencies.length > 0 && (
              <Grid item xs={3}>
                <p className={classes.currencyFormControlTitle}>{translation.text_6276}</p>

                <FormControl variant="outlined" className={commonClasses.formControl}>
                  <TextField
                    value={selectedCurrency}
                    select
                    onChange={(event) => handleCurrencyChange(event.target.value)}
                    className={`${classes.selectContainer} ${theme.direction === 'rtl' ? classes.rtlIcon : ''}`}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">{getSymbolFromCurrency(selectedCurrency)}</InputAdornment>,
                    }}
                    SelectProps={{
                      IconComponent: ExpandMore,
                    }}
                  >
                    {currencies.map((currency) => (
                      <MenuItem className={commonClasses.optionSelector} key={currency} value={currency}>
                        {currency}
                      </MenuItem>
                    ))}
                  </TextField>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={3}>
              <p className={classes.currencyFormControlTitle}>{translation.clients}</p>

              <FormControl variant="outlined" className={commonClasses.formControl}>
                <SelectControl value={selectedClientFilterType} onChange={handleClientFilterTypeChange}>
                  <MenuItem className={commonClasses.optionSelector} value={ClientFilterType.All}>
                    {translation.show_all}
                  </MenuItem>

                  <MenuItem className={commonClasses.optionSelector} value={ClientFilterType.Direct}>
                    {translation.direct}
                  </MenuItem>

                  <MenuItem className={commonClasses.optionSelector} value={ClientFilterType.SubPartnership}>
                    {translation.sub_partnership}
                  </MenuItem>
                </SelectControl>
              </FormControl>
            </Grid>
          </Grid>

          <Grid container direction="row" className={`${classes.filterContainer} ${classes.mobileContainer}`}>
            <Grid item>
              <p className={commonClasses.formControlTitle}>{translation.text_6166}</p>

              <DateRangeSelect defaultValue={selectedDateRange} onRangeChange={setSelectedDateRange} />
            </Grid>
          </Grid>

          <Grid container direction="row" spacing={2}>
            <Grid
              item
              style={{
                marginInlineEnd: 10,
                paddingTop: 0,
              }}
            >
              <Button className={classes.leftRightBtn} onClick={() => handleNav('left')}>
                <ChevronLeft />
              </Button>
            </Grid>

            <Grid container direction="row" spacing={2} ref={navRef} className={classes.quickReportsPanel}>
              <Grid item className={classes.quickReports}>
                <QuickReport
                  title={translation.text_6277}
                  tooltip={translation.total_commission}
                  currency={selectedCurrency}
                  value={aggregatedValues.commissions}
                  percentage={aggregatedValues.commissionsChangePercent}
                  selected={selectedPanel === 'Commissions'}
                  onClick={() => setSelectedPanel('Commissions')}
                />
              </Grid>

              <Grid item className={classes.quickReports}>
                <DoubleQuickReport
                  title={translation.clients_accounts}
                  tooltip={translation.clients_accounts_tooltip}
                  firstValue={aggregatedValues.clients}
                  firstPercentage={aggregatedValues.clientsPercent}
                  secondValue={aggregatedValues.accounts}
                  secondPercentage={aggregatedValues.accountsPercent}
                  selected={selectedPanel === 'Clients & Accounts'}
                  onClick={() => setSelectedPanel('Clients & Accounts')}
                />
              </Grid>

              {SHOW_NEW_DASHBOARD_CARDS ? (
                <>
                  {/* NEW FLOW */}

                  {/* <Grid item className={classes.quickReports}>
                    <QuickReport
                      title={tradingAccountsDepositsTitle}
                      tooltip={tradingAccountsDepositsTitle}
                      currency={selectedCurrency}
                      value={aggregatedValues.internalTransferIn}
                      percentage={aggregatedValues.internalTransferInChangePercent}
                      selected={selectedPanel === 'Trading Accounts Deposits'}
                      onClick={() => setSelectedPanel('Trading Accounts Deposits')}
                    />
                  </Grid> */}

                  <Grid item className={classes.quickReports}>
                    <QuickReport
                      title={tradingAccountsWithdrawalsTitle}
                      tooltip={tradingAccountsWithdrawalsTitle}
                      currency={selectedCurrency}
                      value={aggregatedValues.internalTransferOut}
                      percentage={aggregatedValues.internalTransferOutChangePercent}
                      selected={selectedPanel === 'Trading Accounts Withdrawals'}
                      onClick={() => setSelectedPanel('Trading Accounts Withdrawals')}
                    />
                  </Grid>

                  <Grid item className={classes.quickReports}>
                    <QuickReport
                      title={netTradingAccountsDepositsTitle}
                      tooltip={netTradingAccountsDepositsTitle}
                      currency={selectedCurrency}
                      value={aggregatedValues.netInternalTransfer}
                      percentage={aggregatedValues.netInternalTransferChangePercent}
                      selected={selectedPanel === 'Net Trading Accounts Deposits'}
                      onClick={() => setSelectedPanel('Net Trading Accounts Deposits')}
                    />
                  </Grid>

                  <Grid item className={classes.quickReports}>
                    <QuickReport
                      title={portfolioDepositsTitle}
                      tooltip={translation.portfolio_deposits_tooltip || portfolioDepositsTitle}
                      currency={selectedCurrency}
                      value={aggregatedValues.deposits}
                      percentage={aggregatedValues.depositsChangePercent}
                      selected={selectedPanel === 'Portfolio Deposits'}
                      onClick={() => setSelectedPanel('Portfolio Deposits')}
                    />
                  </Grid>

                  <Grid item className={classes.quickReports}>
                    <QuickReport
                      title={portfolioWithdrawalsTitle}
                      tooltip={translation.portfolio_withdrawals_tooltip || portfolioWithdrawalsTitle}
                      currency={selectedCurrency}
                      value={aggregatedValues.withdrawals}
                      percentage={aggregatedValues.withdrawalsChangePercent}
                      selected={selectedPanel === 'Portfolio Withdrawals'}
                      onClick={() => setSelectedPanel('Portfolio Withdrawals')}
                    />
                  </Grid>

                  <Grid item className={classes.quickReports}>
                    <QuickReport
                      title={netPortfolioDepositsTitle}
                      tooltip={translation.net_portfolio_deposits_tooltip || netPortfolioDepositsTitle}
                      currency={selectedCurrency}
                      value={aggregatedValues.netDeposits}
                      percentage={aggregatedValues.netDepositsChangePercent}
                      selected={selectedPanel === 'Net Portfolio Deposits'}
                      onClick={() => setSelectedPanel('Net Portfolio Deposits')}
                    />
                  </Grid>
                </>
              ) : (
                <>
                  {/* CURRENT PROD FLOW */}

                  <Grid item className={classes.quickReports}>
                    <QuickReport
                      title={translation.deposits}
                      tooltip={translation.deposits_tooltip}
                      currency={selectedCurrency}
                      value={aggregatedValues.deposits}
                      percentage={aggregatedValues.depositsChangePercent}
                      selected={selectedPanel === 'Deposits'}
                      onClick={() => setSelectedPanel('Deposits')}
                    />
                  </Grid>

                  <Grid item className={classes.quickReports}>
                    <QuickReport
                      title={translation.text_271}
                      tooltip={translation.withdrawal_tooltip}
                      currency={selectedCurrency}
                      value={aggregatedValues.withdrawals}
                      percentage={aggregatedValues.withdrawalsChangePercent}
                      selected={selectedPanel === 'Withdrawals'}
                      onClick={() => setSelectedPanel('Withdrawals')}
                    />
                  </Grid>

                  <Grid item className={classes.quickReports}>
                    <QuickReport
                      title={translation.text_270}
                      tooltip={translation.net_deposit_tooltip}
                      currency={selectedCurrency}
                      value={aggregatedValues.netDeposits}
                      percentage={aggregatedValues.netDepositsChangePercent}
                      selected={selectedPanel === 'Net Deposits'}
                      onClick={() => setSelectedPanel('Net Deposits')}
                    />
                  </Grid>
                </>
              )}

              <Grid item className={classes.quickReports}>
                <CPQuickReport
                  title={translation.text_811}
                  translation={translation}
                  tooltip={translation.closed_positions_tooltip}
                  currency={selectedCurrency}
                  firstValue={aggregatedValues.volume}
                  firstPercentage={aggregatedValues.volumeChangePercent}
                  secondValue={aggregatedValues.volumeLots}
                  secondPercentage={aggregatedValues.volumeLotsChangePercent}
                  selected={selectedPanel === 'Closed Positions'}
                  onClick={() => setSelectedPanel('Closed Positions')}
                />
              </Grid>

              <Grid item className={classes.quickReports}>
                <DoubleQuickReport
                  title={translation.clientsProfitLoss}
                  tooltip={translation.profit_loss_tooltip}
                  currency={selectedCurrency}
                  firstValue={aggregatedValues.profit}
                  firstPercentage={aggregatedValues.profitChangePercent}
                  secondValue={aggregatedValues.loss}
                  secondPercentage={aggregatedValues.lossChangePercent}
                  selected={selectedPanel === 'Clients P&L'}
                  onClick={() => setSelectedPanel('Clients P&L')}
                />
              </Grid>
            </Grid>

            <Grid
              item
              style={{
                marginInlineStart: 10,
                paddingTop: 0,
              }}
            >
              <Button className={classes.leftRightBtn} onClick={() => handleNav('right')}>
                <ChevronRight />
              </Button>
            </Grid>
          </Grid>

          <Grid item xs={12} className={classes.container}>
            {selectedPanel === 'Commissions' && (
              <GraphPanel
                clientFilterType={selectedClientFilterType}
                dateRange={selectedDateRange}
                currency={selectedCurrency}
                partnership={selectedPartnership}
                highlightType={HighlightTypes.Commissions}
                legend={translation.text_6293}
                emptyMessage={translation.text_6294}
                firstButtonPath={PATH_COMMISSIONS_BY_CLIENT}
                firstButtonName={translation.view_commission_report}
              />
            )}

            {selectedPanel === 'Clients & Accounts' && (
              <DoubleGraphPanel
                colors={CLIENTS_ACCOUNTS_COLORS}
                valueType="count"
                clientFilterType={selectedClientFilterType}
                dateRange={selectedDateRange}
                emptyMessage={translation.clients_accounts_no_data}
                firstButtonName={translation.view_clients_overview}
                firstButtonPath={PATH_CLIENTS_LIST}
                highlightTypes={CLIENTS_ACCOUNTS_HIGHLIGHT_TYPES}
                legendFirstItem={translation.clients}
                legendSecondItem={translation.text_6388}
                partnership={selectedPartnership}
                order={CLIENTS_ACCOUNTS_ORDER}
              />
            )}

            {!SHOW_NEW_DASHBOARD_CARDS && selectedPanel === 'Deposits' && (
              <GraphPanel
                clientFilterType={selectedClientFilterType}
                dateRange={selectedDateRange}
                currency={selectedCurrency}
                partnership={selectedPartnership}
                highlightType={HighlightTypes.Deposits}
                legend={translation.text_269}
                emptyMessage={translation.depositss_no_data}
                firstButtonPath={PATH_TRADING_SUMMARY}
                firstButtonName={translation.view_trading_summary}
              />
            )}

            {!SHOW_NEW_DASHBOARD_CARDS && selectedPanel === 'Withdrawals' && (
              <GraphPanel
                clientFilterType={selectedClientFilterType}
                dateRange={selectedDateRange}
                currency={selectedCurrency}
                partnership={selectedPartnership}
                highlightType={HighlightTypes.Withdrawals}
                legend={translation.text_271}
                emptyMessage={translation.withdrawals_no_data}
                firstButtonPath={PATH_TRADING_SUMMARY}
                firstButtonName={translation.view_trading_summary}
              />
            )}

            {!SHOW_NEW_DASHBOARD_CARDS && selectedPanel === 'Net Deposits' && (
              <GraphPanel
                clientFilterType={selectedClientFilterType}
                dateRange={selectedDateRange}
                currency={selectedCurrency}
                partnership={selectedPartnership}
                highlightType={HighlightTypes.NetDeposits}
                legend={translation.text_270}
                emptyMessage={translation.net_deposits_no_data}
                firstButtonPath={PATH_TRADING_SUMMARY}
                firstButtonName={translation.view_trading_summary}
              />
            )}

            {SHOW_NEW_DASHBOARD_CARDS && selectedPanel === 'Trading Accounts Deposits' && (
              <GraphPanel
                clientFilterType={selectedClientFilterType}
                dateRange={selectedDateRange}
                currency={selectedCurrency}
                partnership={selectedPartnership}
                highlightType={HighlightTypes.InternalTransferIn}
                legend={tradingAccountsDepositsTitle}
                emptyMessage={translation.trading_accounts_deposits_no_data || translation.text_6294}
                firstButtonPath={PATH_TRADING_SUMMARY}
                firstButtonName={translation.view_trading_summary}
              />
            )}

            {SHOW_NEW_DASHBOARD_CARDS && selectedPanel === 'Trading Accounts Withdrawals' && (
              <GraphPanel
                clientFilterType={selectedClientFilterType}
                dateRange={selectedDateRange}
                currency={selectedCurrency}
                partnership={selectedPartnership}
                highlightType={HighlightTypes.InternalTransferOut}
                legend={tradingAccountsWithdrawalsTitle}
                emptyMessage={translation.trading_accounts_withdrawals_no_data || translation.text_6294}
                firstButtonPath={PATH_TRADING_SUMMARY}
                firstButtonName={translation.view_trading_summary}
              />
            )}

            {SHOW_NEW_DASHBOARD_CARDS && selectedPanel === 'Net Trading Accounts Deposits' && (
              <GraphPanel
                clientFilterType={selectedClientFilterType}
                dateRange={selectedDateRange}
                currency={selectedCurrency}
                partnership={selectedPartnership}
                highlightType={HighlightTypes.NetInternalTransfer}
                legend={netTradingAccountsDepositsTitle}
                emptyMessage={translation.net_trading_accounts_deposits_no_data || translation.text_6294}
                firstButtonPath={PATH_TRADING_SUMMARY}
                firstButtonName={translation.view_trading_summary}
              />
            )}

            {SHOW_NEW_DASHBOARD_CARDS && selectedPanel === 'Portfolio Deposits' && (
              <GraphPanel
                clientFilterType={selectedClientFilterType}
                dateRange={selectedDateRange}
                currency={selectedCurrency}
                partnership={selectedPartnership}
                highlightType={HighlightTypes.Deposits}
                legend={portfolioDepositsTitle}
                emptyMessage={translation.portfolio_deposits_no_data || translation.text_6294}
                firstButtonPath={PATH_TRADING_SUMMARY}
                firstButtonName={translation.view_trading_summary}
              />
            )}

            {SHOW_NEW_DASHBOARD_CARDS && selectedPanel === 'Portfolio Withdrawals' && (
              <GraphPanel
                clientFilterType={selectedClientFilterType}
                dateRange={selectedDateRange}
                currency={selectedCurrency}
                partnership={selectedPartnership}
                highlightType={HighlightTypes.Withdrawals}
                legend={portfolioWithdrawalsTitle}
                emptyMessage={translation.portfolio_withdrawals_no_data || translation.text_6294}
                firstButtonPath={PATH_TRADING_SUMMARY}
                firstButtonName={translation.view_trading_summary}
              />
            )}

            {SHOW_NEW_DASHBOARD_CARDS && selectedPanel === 'Net Portfolio Deposits' && (
              <GraphPanel
                clientFilterType={selectedClientFilterType}
                dateRange={selectedDateRange}
                currency={selectedCurrency}
                partnership={selectedPartnership}
                highlightType={HighlightTypes.NetDeposits}
                legend={netPortfolioDepositsTitle}
                emptyMessage={translation.net_portfolio_deposits_no_data || translation.text_6294}
                firstButtonPath={PATH_TRADING_SUMMARY}
                firstButtonName={translation.view_trading_summary}
              />
            )}

            {selectedPanel === 'Clients P&L' && (
              <DoubleGraphPanel
                colors={PROFIT_LOSS_COLORS}
                clientFilterType={selectedClientFilterType}
                dateRange={selectedDateRange}
                emptyMessage={translation.client_profit_loss_no_data}
                firstButtonName={translation.view_commission_report}
                firstButtonPath={PATH_COMMISSIONS_BY_CLIENT}
                highlightTypes={PROFIT_LOSS_HIGHLIGHT_TYPES}
                legendFirstItem={translation.text_1603}
                legendSecondItem={translation.text_1604}
                partnership={selectedPartnership}
                order={PROFIT_LOSS_ORDER}
              />
            )}

            {selectedPanel === 'Closed Positions' && (
              <DoubleGraphPanel
                colors={CLOSED_POSITIONS_COLORS}
                clientFilterType={selectedClientFilterType}
                dateRange={selectedDateRange}
                emptyMessage={translation.closed_positions_no_data}
                firstButtonName={translation.view_trading_summary}
                firstButtonPath={PATH_TRADING_SUMMARY}
                highlightTypes={CLOSED_POSITIONS_HIGHLIGHT_TYPES}
                legendFirstItem={translation.text_811}
                legendSecondItem={translation.text_696}
                partnership={selectedPartnership}
                order={CLOSED_POSITIONS_ORDER}
              />
            )}
          </Grid>
        </Grid>

        <FiltersDialog open={openFilterDialog} currencies={currencies} translation={translation} partnerships={partnerships} applyFilter={applyFilter} onClose={() => setOpenFilterDialog(false)} />
      </Grid>
    </>
  );
};
